import React, { FC, useCallback, useEffect } from 'react';

import { useStateEffect } from '@common/hooks/useStateEffect';
import { GAMES_LIST } from '@legacyApp/client/components/provablyFair/implementation/GAMES_LIST';
import {
	hiloResult,
	kenoResult,
	limboResult,
	plinkoResult,
	sha256,
} from '@legacyApp/client/modules/app/verificationServcie';
import { trans } from '@legacyApp/client/modules/translation/translate';
import { useDispatchCallback } from '@legacyApp/hooks/store/useAppDispatch';
import { updateForm } from '@legacyApp/client/store/forms/forms.actions';
import { formsReducer } from '@legacyApp/client/store/forms/forms.reducer';
import { GameType } from '@modules/games/Game/constants/availableGames';
import { LinesType } from '@modules/games/PlinkoGame/types';
import { Risk } from '@modules/games/Game/types/GameRisk';
import transactionService from '@modules/transactions/transactionsService';

import GamesStoreController from '@common/components/storeControllers/GamesStoreController';
import { Dropdown } from '@legacyApp/client/components/dropdown/dropdown';
import { InputWrapperContainer } from '@legacyApp/client/components/input/inputWrapper.container';
import { transProvablyFair } from '@legacyApp/client/pages/provablyFair.page/provablyFair.page';
import { GameSlider } from '@modules/games/DiceGame/components/GameSlider';
import { HiloSession } from '@modules/games/HiloGame/HiloSession';
import KenoResult from '@modules/games/KenoGame/components/KenoResult/KenoResult';
import { LimboCounter } from '@modules/games/LimboGame/LimboCounter';
import { LinesSwitch } from '@modules/games/PlinkoGame/components/LinesSwitch/LinesSwitch';
import { RiskSwitch } from '@modules/games/PlinkoGame/components/playground/components/RiskSwitch/RiskSwitch';
import PlinkoResult from '@modules/games/PlinkoGame/components/PlinkoResult';
import { getResultFunction } from '@legacyApp/client/components/provablyFair/verification/getResultFunction';

export const Verification: FC<{
	bet_verification: ReturnType<typeof formsReducer>['bet_verification'];
	setClientSeed: (value: any) => void;
	setServerSeed: (value: any) => void;
	setNonce: (value: any) => void;
	setGame: (value: GameType) => void;
	resetVerificationForm: VoidFunction;
}> = ({
	bet_verification,
	setClientSeed,
	setServerSeed,
	setNonce,
	setGame,
	resetVerificationForm,
}) => {
	const {
		client_seed,
		server_seed,
		nonce,
		game,
		rows: rows_,
		risk,
	} = bet_verification;

	const rows = rows_ as LinesType;

	const updateFormDispatch = useDispatchCallback(updateForm);

	const setRows = useCallback(
		(value: LinesType) => {
			updateFormDispatch({ section: 'bet_verification', field: 'rows', value });
		},
		[updateFormDispatch],
	);

	const setRisk = useCallback(
		(value: Risk) => {
			updateFormDispatch({ section: 'bet_verification', field: 'risk', value });
		},
		[updateFormDispatch],
	);

	const result = useStateEffect(
		useCallback(() => {
			const callback = getResultFunction(game);

			if (!callback) {
				return null;
			}

			return callback(client_seed, server_seed, nonce);
		}, [game, client_seed, server_seed, nonce]),
		transactionService.isDataDifferent,
	);

	const setGame_ = useCallback(
		(game: GameType) => setGame(game.toLowerCase() as GameType),
		[setGame],
	);

	useEffect(
		() => () => {
			resetVerificationForm();
		},
		[resetVerificationForm],
	);

	const server_seed_hashed = server_seed?.length ? sha256(server_seed) : '';

	return (
		<>
			<GamesStoreController />
			<Dropdown
				localTheme={{
					default: true,
				}}
				id={'provablyFairVerificationTab'}
				className="margin-bottom"
				items={GAMES_LIST}
				active={game.toUpperCase()}
				onClick={setGame_}
			/>

			<InputWrapperContainer
				name={'client-seed'}
				value={client_seed}
				label={transProvablyFair({ label: 'Client seed' })}
				onChange={setClientSeed}
				placeholder={'Type client seed'}
			/>

			<InputWrapperContainer
				name={'nonce'}
				value={nonce}
				label={transProvablyFair({ label: 'Nonce' })}
				onChange={setNonce}
				placeholder={'Type nonce'}
			/>

			<InputWrapperContainer
				name={'server-seed'}
				value={server_seed}
				label={transProvablyFair({ label: 'Server seed' })}
				onChange={setServerSeed}
				placeholder={'Type server seed'}
			/>

			{server_seed && typeof server_seed_hashed === 'string' && (
				<InputWrapperContainer
					name={'server-seed-hashed'}
					value={server_seed_hashed}
					label={trans({ label: 'Server seed (hashed)' })}
					placeholder={''}
					isDisabled={true}
				/>
			)}

			{game === GameType.PLINKO && (
				<div className="box-no-height">
					<LinesSwitch compactView value={rows} update={setRows} />
					<RiskSwitch compactView value={risk} update={setRisk} />
				</div>
			)}

			{result && typeof result.data !== 'object' && game === GameType.DICE && (
				<div className="box-no-height margin-top">
					<GameSlider
						rule={null}
						game={game}
						disable
						betValues={[]}
						className="width-100 no-padding no-margin small-margin-bottom"
						result={{
							value: result.data,
							state: 'win',
						}}
					/>
				</div>
			)}

			{result &&
				result.data.length &&
				typeof result.data === 'object' &&
				game &&
				game.indexOf(GameType.HILO) > -1 && (
					<div className="game--hilo box-no-height no-padding margin-top">
						<HiloSession
							className="width-100 no-margin"
							static={true}
							noScroll={true}
							session={result.data.map((el, index) => ({
								result_value: hiloResult(el),
								sub_nonce: index,
							}))}
						/>
					</div>
				)}

			{result && typeof result.data !== 'object' && game === GameType.LIMBO && (
				<div className="game--limbo box-no-height no-padding margin-top">
					<LimboCounter result={limboResult(result.data)} animation={true} />
				</div>
			)}

			{result &&
				result.data.length &&
				typeof result.data === 'object' &&
				game === GameType.PLINKO && (
					<div className="box-no-height no-padding margin-top">
						<span className="text-style-sm-medium">
							Payout index:{' '}
							{plinkoResult(result.data, risk, rows).path.join('+')}={''}
							{plinkoResult(result.data, risk, rows).result}
						</span>

						<PlinkoResult
							result={plinkoResult(result.data, risk, rows).result}
							risk={risk}
							rows={rows}
						/>
					</div>
				)}

			{result &&
				result.data.length &&
				typeof result.data === 'object' &&
				game === GameType.KENO && (
					<div className="box-no-height mt-[20px]">
						<KenoResult
							data={{
								hash: null,
								risk: Risk.LOW,
								selected: kenoResult(result.data),
								hits: kenoResult(result.data),
								dataType: null,
							}}
							fullPanel
						/>
					</div>
				)}
		</>
	);
};
